/**
 * 짐 스펙 중 문서가 실제로 읽는 필드만.
 *
 * `application` 의 `Spec` 을 그대로 가져오지 않는 이유: 그러면 이 패키지가 앱의 엔티티
 * 계층에 묶여 프리뷰도 그 트리를 통째로 끌어와야 한다. 구조적 타입으로 두면 앱의
 * `Spec` 이 그대로 대입되고(더 넓은 타입이므로), 프리뷰는 API 응답만 있으면 된다.
 *
 * 필드를 더 읽어야 하면 여기 추가한다. 여기 없는 필드는 문서가 못 본다는 뜻이다.
 */

/** 속성 하나에 걸리는 규칙. 수치 비교·불리언·열거 값 포함 여부 등. */
export interface ConditionRule {
  type: string;
  threshold?: number;
  value?: boolean;
  values?: string[];
  /** AND: 같은 속성에 겹쳐 거는 하위 규칙. */
  rules?: ConditionRule[];
  /** CROSS_AND: 여러 속성에 걸친 복합 조건. */
  clauses?: AttributeClause[];
}

export interface AttributeClause {
  key: string;
  rule: ConditionRule;
}

/** 규칙이 맞았을 때 일어나는 일 — 안내 문구, 서비스 범위 제한 등. */
export interface ConditionEffect {
  type: string;
  message?: string;
  optionKey?: string;
  label?: string;
  allowedServiceSets?: string[];
  allowedServiceTypeKeys?: string[];
}

export interface AttributeCondition {
  rule: ConditionRule;
  effect: ConditionEffect;
}

export interface AttrDef {
  key: string;
  name?: string;
  /** STRING | NUMBER | BOOLEAN | ENUM */
  type: string;
  values?: string[];
  enumDisplayNames?: Record<string, string>;
  conditions?: AttributeCondition[];
}

/** 조건부 표시명 규칙. 단위(suffix) 추출에 쓰인다. */
export interface DisplayTemplateRule {
  template?: string | null;
  rule?: ConditionRule;
}

export interface ItemSpec {
  id: string;
  name: string;
  /** 아이콘 다운로드 URL. 조회 시 서버가 준다. */
  iconDownloadUrl?: string;
  /** 섹션 분류용 방 타입 태그. 비어 있으면 공간 무관 품목. */
  roomTypes?: readonly string[];
  /** 앱바 'i' 아이콘에 붙는 설명. */
  description?: string;
  /** 짐 상세에서 물을 속성들. 없으면 서비스 범위만 묻는다. */
  attributes?: AttrDef[];
  /** 속성에 매달리지 않는 스펙 공통 조건. 속성이 없는 품목도 규칙을 걸 수 있다. */
  conditions?: AttributeCondition[];
  displayNameTemplate?: string | null;
  displayTemplateRules?: DisplayTemplateRule[];
}

/** 짐 상세 플로우를 만들 때 쓰는 이름. 앱의 `Spec` 과 같은 자리다. */
export type Spec = ItemSpec;

/**
 * 서버 응답을 문서가 읽는 모양으로 맞춘다.
 *
 * ENUM 속성만 응답 형태가 다르다 — 서버는 `type: { type, values, enumDisplayNames }`
 * 로 감싸 주는데 문서는 `type: 'ENUM'` 과 나란한 `values` 를 본다. 정규화를 빠뜨리면
 * 열거형 질문이 선택지 대신 빈 텍스트 입력으로 떨어진다.
 */
export function normalizeAttrDef(raw: Record<string, unknown>): AttrDef {
  const typeVal = raw.type;
  if (typeof typeVal === "object" && typeVal !== null && "type" in typeVal) {
    const t = typeVal as {
      type?: string;
      values?: string[];
      enumDisplayNames?: Record<string, string>;
    };
    if (t.type === "ENUM") {
      return {
        key: String(raw.key ?? ""),
        ...(raw.name != null ? { name: String(raw.name) } : {}),
        type: "ENUM",
        values: Array.isArray(t.values) ? t.values : [],
        ...(t.enumDisplayNames
          ? { enumDisplayNames: t.enumDisplayNames }
          : {}),
        ...(Array.isArray(raw.conditions)
          ? { conditions: raw.conditions as AttributeCondition[] }
          : {}),
      };
    }
    if (t.type) {
      return { ...(raw as unknown as AttrDef), type: t.type };
    }
  }
  return raw as unknown as AttrDef;
}

/** 스펙 한 건 정규화. 속성 배열만 손보고 나머지는 그대로 둔다. */
export function normalizeItemSpec(raw: Record<string, unknown>): ItemSpec {
  const attrs = Array.isArray(raw.attributes) ? raw.attributes : [];
  return {
    ...(raw as unknown as ItemSpec),
    attributes: attrs.map((a) =>
      normalizeAttrDef(
        typeof a === "object" && a ? (a as Record<string, unknown>) : {},
      ),
    ),
  };
}
